import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import InputMask from "react-input-mask";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import Select from "react-select";
import { toast } from "react-toastify";
import { styles } from "../../../Utils/Common";
import { formatForInputDate, getStatusText, toNumberOrNull } from "./helpers";
import {
  oncologySupplyQueryKeys,
  useOncologyFacilitiesLookup,
  useOncologyOrderDetail,
  useOncologyStatesLookup,
} from "./hooks";
import { SupplyOrder } from "./types";
import SupplyManagmentService from "Services/SupplyManagement/SupplyManagmentService";
import NoRecord from "Shared/Common/NoRecord";
import { Loader } from "Shared/Common/Loader";
import useLang from "Shared/hooks/useLanguage";
import { PermissionObject } from "Shared/Common/Permissions/PermissionComponent";

type ShippingFieldErrors = Partial<
  Record<"shippingAddress" | "city" | "stateId" | "zipCode", string>
>;

type PermissionState = {
  Reducer?: {
    selectedTenantInfo?: {
      infomationOfLoggedUser?: {
        permissions?: PermissionObject[];
      };
    };
  };
};

function OrderDetail() {
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { id } = location.state || {};
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [order, setOrder] = useState<SupplyOrder | null>(null);
  const [errors, setErrors] = useState<ShippingFieldErrors>({});
  const canUpdateOrders = useSelector((state: PermissionState) =>
    (
      state.Reducer?.selectedTenantInfo?.infomationOfLoggedUser?.permissions ??
      []
    ).some(
      (permission) =>
        permission.subject.replace(/\n/g, "").toLowerCase() ===
          "Manage Order (Oncology)".toLowerCase() &&
        permission.moduleName.replace(/\n/g, "").toLowerCase() ===
          "Supply Management".toLowerCase() &&
        permission.action.replace(/\n/g, "").toLowerCase() ===
          "Update".toLowerCase()
    )
  );

  const {
    data: orderData,
    isLoading: orderLoading,
    isFetching: orderFetching,
  } = useOncologyOrderDetail(id);
  const { data: facilities = [] } = useOncologyFacilitiesLookup();
  const { data: states = [] } = useOncologyStatesLookup();

  useEffect(() => {
    if (!id) {
      navigate("/manage-order-oncology");
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!orderData) return;

    setOrder(orderData);
  }, [orderData]);

  const saveOrderMutation = useMutation(SupplyManagmentService.saveOrder, {
    onSuccess: (res: any) => {
      if (res?.data?.httpStatusCode === 200) {
        toast.success(res?.data?.message);
        queryClient.invalidateQueries(oncologySupplyQueryKeys.ordersRoot());
        queryClient.invalidateQueries(oncologySupplyQueryKeys.order(id));
        navigate("/manage-order-oncology", {
          state: { shipped: getStatusText(order).toLowerCase() },
        });
        return;
      }

      toast.error(res?.data?.message || t("Unable to update order"));
    },
    onError: (error: any) => {
      console.error(error);
      toast.error(
        error?.response?.data?.message || t("Unable to update order")
      );
    },
  });

  const rejectMutation = useMutation(
    SupplyManagmentService.saveRejectionDetail,
    {
      onSuccess: (res: any) => {
        if (res?.data?.httpStatusCode === 200) {
          toast.success(res?.data?.message);
          setShowRejectModal(false);
          queryClient.invalidateQueries(oncologySupplyQueryKeys.ordersRoot());
          queryClient.invalidateQueries(oncologySupplyQueryKeys.order(id));
          navigate("/manage-order-oncology");
          return;
        }

        toast.error(res?.data?.message || t("Unable to reject order"));
      },
      onError: (error: any) => {
        console.error(error);
        toast.error(
          error?.response?.data?.message || t("Unable to reject order")
        );
      },
    }
  );

  const handleKeyPress = (e: React.KeyboardEvent) => {
    const keyCode = e.charCode || e.keyCode;
    const allowedKeyCodes = [8, 9, 37, 38, 39, 40, 46];
    if ((keyCode < 48 || keyCode > 57) && !allowedKeyCodes.includes(keyCode)) {
      e.preventDefault();
    }
  };

  const handleEnterPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const updateOrder = (patch: Partial<SupplyOrder>) => {
    setOrder((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const clearError = (field: keyof ShippingFieldErrors) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;

      return {
        ...prev,
        [field]: "",
      };
    });
  };

  const updateItem = (index: number, patch: any) => {
    setOrder((prev) => {
      if (!prev) return prev;
      const supplyOrderItems = [...(prev.supplyOrderItems ?? [])];
      supplyOrderItems[index] = {
        ...supplyOrderItems[index],
        ...patch,
      };
      return { ...prev, supplyOrderItems };
    });
  };

  const isApprovedQuantityDisabled =
    !canUpdateOrders || Number(order?.status ?? order?.statusId) >= 3;

  const updateApprovedQuantity = (index: number, value: string) => {
    if (isApprovedQuantityDisabled) return;

    const requestedQuantity = toNumberOrNull(
      order?.supplyOrderItems?.[index]?.orderQuantityRequested
    );
    const approvedQuantity = toNumberOrNull(value);

    if (value === "") {
      updateItem(index, { orderQuantityApproved: "" });
      return;
    }

    if (approvedQuantity === null) return;

    const nextValue =
      requestedQuantity !== null && approvedQuantity > requestedQuantity
        ? requestedQuantity
        : approvedQuantity;

    updateItem(index, {
      orderQuantityApproved: nextValue,
    });
  };

  const hasInvalidApprovedQuantity = (targetOrder: SupplyOrder) =>
    (targetOrder.supplyOrderItems ?? []).some((item) => {
      const requestedQuantity = toNumberOrNull(item.orderQuantityRequested);
      const approvedQuantity = toNumberOrNull(item.orderQuantityApproved);

      return (
        approvedQuantity === null ||
        approvedQuantity <= 0 ||
        (requestedQuantity !== null && approvedQuantity > requestedQuantity)
      );
    });

  const isEmpty = (value?: string | number | null) =>
    `${value ?? ""}`.trim() === "";

  const validate = (targetOrder: SupplyOrder | null = order) => {
    if (!targetOrder?.facilityId) {
      toast.error(t("Please select the facility name."));
      return false;
    }

    const nextErrors: ShippingFieldErrors = {};

    if (isEmpty(targetOrder.shippingAddress)) {
      nextErrors.shippingAddress = t("Please enter the shipping address.");
    }

    if (isEmpty(targetOrder.city)) {
      nextErrors.city = t("Please enter the city.");
    }

    if (!targetOrder.stateId) {
      nextErrors.stateId = t("Please select the state.");
    }

    if (isEmpty(targetOrder.zipCode)) {
      nextErrors.zipCode = t("Please enter the zip code.");
    }

    setErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean)) {
      return false;
    }

    if (hasInvalidApprovedQuantity(targetOrder)) {
      toast.error(
        t(
          "Quantity approved must be greater than 0 and cannot exceed quantity requested."
        )
      );
      return false;
    }

    return true;
  };

  const saveOrder = (payloadOrder: SupplyOrder | null = order) => {
    if (
      !canUpdateOrders ||
      saveOrderMutation.isLoading ||
      !payloadOrder ||
      !validate(payloadOrder)
    ) {
      return;
    }

    saveOrderMutation.mutate({
      // dateofRequest is sent back exactly as received so its time is kept.
      ...payloadOrder,
      supplyOrderItems: (payloadOrder.supplyOrderItems ?? []).map((item) => ({
        ...item,
        orderQuantityRequested: toNumberOrNull(item.orderQuantityRequested),
        orderQuantityApproved: toNumberOrNull(item.orderQuantityApproved),
      })),
    });
  };

  const cancelOrder = () => {
    navigate("/manage-order-oncology", {
      state: { shipped: getStatusText(order).toLowerCase() },
    });
  };

  const rejectNow = () => {
    if (!canUpdateOrders || rejectMutation.isLoading) return;

    if (!order?.rejectedReason) {
      toast.error(t("Please fill the required details"));
      return;
    }

    rejectMutation.mutate({
      ids: [id],
      rejectedReason: order.rejectedReason,
    });
  };

  if (orderLoading && !order) {
    return <Loader />;
  }

  return (
    <>
      <Modal
        show={canUpdateOrders && showRejectModal}
        onHide={() => setShowRejectModal(false)}
        backdrop="static"
        keyboard={false}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="m-0 p-6">
          <h4>{t("Rejection Reason")}</h4>
        </Modal.Header>
        <Modal.Body>
          <div className="mt-5">
            <div className="fv-row mb-4">
              <label className="required mb-2 fw-500">
                {t("Rejection Reason")}
              </label>
              <input
                type="text"
                name="rejectedReason"
                onChange={(e) =>
                  updateOrder({ rejectedReason: e.target.value })
                }
                className="form-control"
                placeholder={t("Rejection Reason")}
                value={order?.rejectedReason ?? ""}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-3 px-6">
          <button
            type="button"
            className="badge badge-pill badge-secondary py-3 px-4 border-0 fw-400 fa-1x"
            onClick={() => setShowRejectModal(false)}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="badge badge-pill badge-danger py-3 px-4 border-0 fw-400 fa-1x text-light"
            onClick={rejectNow}
          >
            {rejectMutation.isLoading ? t("Rejecting...") : t("Reject")}
          </button>
        </Modal.Footer>
      </Modal>

      <form onSubmit={(e) => e.preventDefault()} onKeyDown={handleEnterPress}>
        <div id="kt_app_toolbar" className="app-toolbar py-2 pt-lg-3">
          <div
            id="kt_app_toolbar_container"
            className="app-container container-fluid d-flex flex-wrap gap-4 justify-content-center justify-content-sm-between align-items-center"
          >
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <ul className="breadcrumb breadcrumb-separatorless fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="" className="text-muted text-hover-primary">
                    {t("Home")}
                  </a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  {t("Supply Management")}
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  {t("Oncology Supply Orders")}
                </li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              {canUpdateOrders && (
                <>
                  <button
                    className="btn btn-sm fw-bold btn-primary"
                    type="button"
                    onClick={() => saveOrder()}
                    disabled={saveOrderMutation.isLoading}
                  >
                    {saveOrderMutation.isLoading ? t("Saving...") : t("Update")}
                  </button>
                  <button
                    className="btn btn-sm fw-bold btn-danger"
                    type="button"
                    onClick={() => setShowRejectModal(true)}
                    disabled={rejectMutation.isLoading}
                  >
                    {t("Reject")}
                  </button>
                </>
              )}
              <button
                className="btn btn-secondary btn-sm fw-bold"
                type="button"
                onClick={cancelOrder}
              >
                <span>{t("Cancel")}</span>
              </button>
            </div>
          </div>
        </div>
        <div className="app-container container-fluid">
          <div id="OncologyShippingInformationView" className="card mb-5">
            <div className="align-items-center card-header d-flex justify-content-center justify-content-sm-between gap-1">
              <h4 className="m-1">{t("Shipping Information")}</h4>
              {orderFetching && (
                <span className="text-muted fs-8">{t("Refreshing...")}</span>
              )}
            </div>
            <div className="card-body">
              <div className="mb-5">
                <span className="text-primary fw-700">{t("Shipping to:")}</span>{" "}
                <span className="text-primary fw-500">
                  {order?.shippingAddress}
                </span>
              </div>
              <div className="row">
                <EditableInput
                  label="Date of Request"
                  type="date"
                  value={formatForInputDate(order?.dateofRequest)}
                  onChange={(value) => updateOrder({ dateofRequest: value })}
                  disabled
                />
                <EditableInput
                  label="Representative Name"
                  value={order?.representativeName ?? ""}
                  onChange={(value) =>
                    updateOrder({ representativeName: value })
                  }
                  disabled
                />
                <div className="col-xl-3 col-md-4 col-sm-6 col-12">
                  <div className="fv-row mb-4">
                    <label className="mb-2 fw-500">
                      {t("Representative Phone Number")}
                    </label>
                    <InputMask
                      mask="(999) 999 9999"
                      value={order?.representativePhone ?? ""}
                      type="tel"
                      name="representativePhone"
                      onChange={(e) =>
                        updateOrder({ representativePhone: e.target.value })
                      }
                      onKeyDown={handleKeyPress}
                      className="form-control bg-transparent"
                      placeholder="(999) 999 9999"
                      inputMode="numeric"
                    />
                  </div>
                </div>
                <EditableInput
                  label="Shipping Address"
                  value={order?.shippingAddress ?? ""}
                  onChange={(value) => {
                    updateOrder({ shippingAddress: value });
                    clearError("shippingAddress");
                  }}
                  error={errors.shippingAddress}
                />
                <EditableInput
                  label="City"
                  value={order?.city ?? ""}
                  onChange={(value) => {
                    updateOrder({ city: value });
                    clearError("city");
                  }}
                  error={errors.city}
                />
                <div className="col-xl-3 col-md-4 col-sm-6 col-12">
                  <div className="fv-row mb-4">
                    <label className="mb-2 fw-500 required">{t("State")}</label>
                    <Select
                      menuPortalTarget={document.body}
                      options={states}
                      theme={(theme: any) => styles(theme)}
                      placeholder={t("State")}
                      name="stateId"
                      value={states.filter(
                        (item) => Number(item.value) === Number(order?.stateId)
                      )}
                      onChange={(event: any) => {
                        updateOrder({ stateId: Number(event.value) });
                        clearError("stateId");
                      }}
                    />
                    {errors.stateId && (
                      <div className="text-danger fs-8 mt-1">
                        {errors.stateId}
                      </div>
                    )}
                  </div>
                </div>
                <EditableInput
                  label="Zip Code"
                  maxLength={5}
                  value={order?.zipCode ?? ""}
                  onChange={(value) => {
                    updateOrder({ zipCode: value });
                    clearError("zipCode");
                  }}
                  onKeyDown={handleKeyPress}
                  error={errors.zipCode}
                />
                <div className="col-xl-3 col-md-4 col-sm-6 col-12">
                  <div className="fv-row mb-4">
                    <label className="mb-2 fw-500">{t("Phone")}</label>
                    <InputMask
                      mask="(999) 999 9999"
                      value={order?.phoneNo ?? ""}
                      type="tel"
                      name="phoneNo"
                      onChange={(e) => updateOrder({ phoneNo: e.target.value })}
                      onKeyDown={handleKeyPress}
                      className="form-control bg-transparent"
                      placeholder="(999) 999 9999"
                      inputMode="numeric"
                    />
                  </div>
                </div>
                <div className="col-xl-3 col-md-4 col-sm-6 col-12">
                  <div className="fv-row mb-4">
                    <label className="mb-2 fw-500 required">
                      {t("Facility Name")}
                    </label>
                    <Select
                      menuPortalTarget={document.body}
                      options={facilities}
                      theme={(theme: any) => styles(theme)}
                      placeholder={t("Facility Name")}
                      name="facilityId"
                      isDisabled
                      value={facilities.filter(
                        (item) =>
                          Number(item.value) === Number(order?.facilityId)
                      )}
                      onChange={(event: any) =>
                        updateOrder({ facilityId: Number(event.value) })
                      }
                    />
                  </div>
                </div>
                <div className="col-12">
                  <div className="border-top pt-3 mt-1 mb-3">
                    <h6 className="mb-1 fw-bold">
                      {t("Kit Shipping Details")}
                    </h6>
                    <span className="text-muted fs-8">
                      {t(
                        "Complete only if shipping details are different from Facility."
                      )}
                    </span>
                  </div>
                </div>
                <EditableInput
                  id="OncologyManageOrderDetailKitFullName"
                  label="Full Name"
                  required={false}
                  value={order?.kitFullName ?? ""}
                  onChange={(value) => updateOrder({ kitFullName: value })}
                />
                <EditableInput
                  id="OncologyManageOrderDetailKitShippingAddress"
                  label="Shipping Address"
                  required={false}
                  value={order?.kitShippingAddress ?? ""}
                  onChange={(value) =>
                    updateOrder({ kitShippingAddress: value })
                  }
                />
                <EditableInput
                  id="OncologyManageOrderDetailKitCity"
                  label="City"
                  required={false}
                  value={order?.kitCity ?? ""}
                  onChange={(value) => updateOrder({ kitCity: value })}
                />
                <EditableInput
                  id="OncologyManageOrderDetailKitState"
                  label="State"
                  required={false}
                  value={order?.kitState ?? ""}
                  onChange={(value) => updateOrder({ kitState: value })}
                />
                <EditableInput
                  id="OncologyManageOrderDetailKitZipCode"
                  label="Zip Code"
                  required={false}
                  value={order?.kitZipCode ?? ""}
                  onChange={(value) => updateOrder({ kitZipCode: value })}
                />
                <EditableInput
                  id="OncologyManageOrderDetailKitPhone"
                  label="Phone"
                  required={false}
                  value={order?.kitPhone ?? ""}
                  onChange={(value) => updateOrder({ kitPhone: value })}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="app-container container-fluid">
          <div className="card mb-5">
            <div className="align-items-center card-header d-flex justify-content-center justify-content-sm-between gap-1">
              <h4 className="m-1">{t("Supplies Information")}</h4>
            </div>
            <div className="align-items-center card-header d-flex justify-content-center justify-content-sm-between gap-1 mt-5 pb-5">
              <Box sx={{ height: "auto", width: "100%" }}>
                <div className="table_bordered overflow-hidden">
                  <TableContainer
                    sx={{
                      maxHeight: "calc(100vh - 100px)",
                      "&::-webkit-scrollbar": { width: 7 },
                      "&::-webkit-scrollbar-track": { backgroundColor: "#fff" },
                      "&:hover": {
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "var(--kt-gray-400)",
                          borderRadius: 2,
                        },
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "var(--kt-gray-400)",
                        borderRadius: 2,
                      },
                    }}
                    className="shadow-none"
                  >
                    <Table className="table table-cutome-expend table-bordered table-sticky-header plate-mapping-table table-bg table-head-custom table-vertical-center border-0 mb-0">
                      <TableHead style={{ zIndex: 0 }}>
                        <TableRow className="h-40px">
                          <TableCell>{t("Item Type")}</TableCell>
                          <TableCell>{t("QTY Requested")}</TableCell>
                          <TableCell>{t("QTY Approved")}</TableCell>
                          <TableCell>{t("Item")}</TableCell>
                          <TableCell>{t("Description")}</TableCell>
                          <TableCell>{t("Special Request")}</TableCell>
                          <TableCell>{t("Comments")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {order?.supplyOrderItems?.length ? (
                          order.supplyOrderItems.map((item, index) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.itemType}</TableCell>
                              <TableCell>
                                <input
                                  type="text"
                                  name="orderQuantityRequested"
                                  className="form-control"
                                  placeholder={t("Order Quantity Requested")}
                                  value={item.orderQuantityRequested ?? ""}
                                  disabled
                                />
                              </TableCell>
                              <TableCell className="position-relative">
                                <input
                                  onKeyDown={handleKeyPress}
                                  type="text"
                                  name="orderQuantityApproved"
                                  onChange={(e) =>
                                    updateApprovedQuantity(
                                      index,
                                      e.target.value
                                    )
                                  }
                                  className="form-control"
                                  placeholder={t("Order Quantity Approved")}
                                  value={item.orderQuantityApproved ?? ""}
                                  disabled={isApprovedQuantityDisabled}
                                />
                              </TableCell>
                              <TableCell>{item.itemName}</TableCell>
                              <TableCell>{item.itemDescription}</TableCell>
                              <TableCell>
                                <textarea
                                  className="form-control fw-400 fs-7 p-2 rounded"
                                  name="specialRequest"
                                  placeholder={t("Special Request")}
                                  rows={2}
                                  value={item.specialRequest ?? ""}
                                  disabled={!canUpdateOrders}
                                  onChange={(e) =>
                                    updateItem(index, {
                                      specialRequest: e.target.value,
                                    })
                                  }
                                ></textarea>
                              </TableCell>
                              <TableCell>
                                <textarea
                                  className="form-control fw-400 fs-7 p-2 rounded"
                                  name="comments"
                                  placeholder={t("Comments")}
                                  rows={2}
                                  value={item.comments ?? ""}
                                  disabled={!canUpdateOrders}
                                  onChange={(e) =>
                                    updateItem(index, {
                                      comments: e.target.value,
                                    })
                                  }
                                ></textarea>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <NoRecord />
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </Box>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

function EditableInput({
  id,
  label,
  value,
  onChange,
  type = "text",
  maxLength,
  onKeyDown,
  disabled,
  error,
  required = true,
}: {
  id?: string;
  label: string;
  value: any;
  onChange: (value: string) => void;
  type?: string;
  maxLength?: number;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  disabled?: boolean;
  error?: string;
  required?: boolean;
}) {
  const { t } = useLang();

  return (
    <div className="col-xl-3 col-md-4 col-sm-6 col-12">
      <div className="fv-row mb-4">
        <label className={`${required ? "required " : ""}mb-2 fw-500`}>
          {t(label)}
        </label>
        <input
          id={id}
          type={type}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          className="form-control bg-transparent"
          placeholder={t(label)}
          value={value ?? ""}
          maxLength={maxLength}
          inputMode={maxLength ? "numeric" : undefined}
          disabled={disabled}
        />
        {error && <div className="text-danger fs-8 mt-1">{error}</div>}
      </div>
    </div>
  );
}

export default OrderDetail;
